import { IUnitedSections, IUnitedSubSections } from 'constants/businessToolSections';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useCheckIsDocShared } from 'hooks/useCheckIsDocShared';
import { useExamplePreview } from 'hooks/useExamplePreview';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { isBusinessToolSection } from 'helpers/businessTool/sections/isBusinessToolSection';
import { isForInvestorsSection } from 'helpers/businessTool/sections/isForInvestorsSection';
import { isOnePageSection } from 'helpers/businessTool/sections/isOnePageSection';

import { forBankAccessibilityRules } from './forBankAccess';
import { forInvestorsAccessibilityRules } from './forInvestorsAccess';
import { ISectionAccess } from './interface';
import { onePageAccessibilityRules } from './onePageAccess';

const getSectionRules = (section: IUnitedSections): ISectionAccess | null => {
  if (isBusinessToolSection(section)) {
    return forBankAccessibilityRules[section];
  }

  if (isOnePageSection(section)) {
    return onePageAccessibilityRules[section];
  }

  if (isForInvestorsSection(section)) {
    return forInvestorsAccessibilityRules[section];
  }

  return null;
};

export const useCheckSubSectionAccess = (section: IUnitedSections, subSection?: IUnitedSubSections): boolean => {
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const subscriptionPlan = userSubscription?.currentSubscription?.subscriptionPlan || SUBSCRIPTION_PLAN.UNAUTHORIZED;
  const isDocumentBeingShared = useCheckIsDocShared();
  const { isExamplePage } = useExamplePreview();

  const sectionRules = getSectionRules(section);

  if (!sectionRules || !subscriptionPlan) {
    return false;
  }

  // If a subSection is provided and defined in the rules, check access specifically for that subSection
  // Otherwise, fall back to section-level access
  const accessLevels = subSection && sectionRules[subSection] ? sectionRules[subSection] : sectionRules._accessPlan;

  // Check if the user's subscription plan is included in the access levels
  return isExamplePage ? true : ((accessLevels?.includes(subscriptionPlan) || isDocumentBeingShared) ?? false);
};
